import { firstDefined } from '@avantia/client-and-server-utilities';
import { createControlId } from '@avantia/questionset-model';
import $ from 'jquery';
import { BaseSyntheticEvent } from 'react';
import { FormControl } from 'react-bootstrap';
import { findLookupKeyFromText, HpSearchTermSuggestionResult } from '../../../tools/listSearchUtility';
import { HpClientQuestion, HpClientQuestionLookupItemMap, HpClientStandardInputTextAndStyle } from '../../../types';
import { HpInputControlProps, HpOnFocusEvent, HpOnMouseEvent } from '../../../types/inputControlTypes';
import { StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { addKeyboardNavigationHandler } from '../controlEvents';

type SlideActionTypes = 'slideUp' | 'slideDown';

const noMatchKey = 'no-matches';
const slideUpAction: SlideActionTypes = 'slideUp';
const slideDownAction: SlideActionTypes = 'slideDown';
const autoCompleteCssSelector = '.hp-auto-complete';
const dropDownMenuCssClass = 'dropdown-menu';
const dropDownItemCssClass = 'dropdown-item';
const dropDownItemCssSelector = '.' + dropDownItemCssClass;

export function AutoCompleteListControl({
  answer,
  answerSource,
  data,
  dataType,
  disabled,
  errors,
  formatType,
  id,
  name,
  onBlur,
  onChange,
  onClick,
  onFocus,
  question,
  questionText,
  subtext,
  type,
  ui,
  value: answerValue
}: HpInputControlProps): JSX.Element {
  const value = answerValue as string;
  const { lookup, textAndStyle: inputTextAndStyle } = data;

  // The blur event is used to locate the correct item from the list (in the event that the option has been typed rather than selected).
  // If an option has been selected from the list, we need to prevent the blur from firing because it causes side effects (sometimes
  // preventing the correct item from being selected).
  let blurId: NodeJS.Timeout | undefined;
  const clearBlurTimeout = (): void => {
    if (blurId) {
      clearTimeout(blurId);
    }
  };

  const changeHandler = createCustomHandler(question, onChange, slideDownAction);
  const focusHandler = createCustomHandler(question, onFocus, slideDownAction);
  const clickHandler = createCustomHandler(question, onClick, slideUpAction, clearBlurTimeout);
  const preludeAction = createChangeEventPrelude(lookup);
  const blurHandler = (e: BaseSyntheticEvent): void => {
    // if we don't copy the event, it will have mutated and become invalid by the time we use it.
    const ecopy = { ...e };
    blurId = setTimeout(() => {
      blurId = undefined;
      createCustomHandler(question, onBlur, slideUpAction, preludeAction)(ecopy);
    }, 50);
  };

  const error = errors ? errors[id] : undefined;
  const textAndStyle = inputTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const { placeholder, noItemsMatchText = 'No matching items' } = textAndStyle;

  function createOption(key: string, text: string) {
    const isMatch = key !== noMatchKey;
    const onClickHandler = isMatch ? (clickHandler as any) : undefined;
    return (
      <button
        key={key}
        onClick={onClickHandler}
        onMouseDown={onClickHandler}
        id={key}
        className={`${dropDownItemCssClass}${isMatch ? '' : ` ${key}`}`}
        type="button"
      >
        {text}
      </button>
    );
  }

  let suggestionList: JSX.Element[] = [];
  if (answer.partial) {
    const { suggestions, minTermLength } = answer.partial as HpSearchTermSuggestionResult;
    if (suggestions.length) {
      suggestionList = suggestions.map(({ key: suggKey, value: suggVal }) => {
        return createOption(suggKey, suggVal);
      });
    } else if (value.length >= minTermLength) {
      suggestionList = [createOption(noMatchKey, noItemsMatchText)];
    }
  }

  const controlId = createControlId({ questionId: id });
  const control = (
    <FormControl
      autoComplete="off"
      className="animated block"
      disabled={disabled}
      id={controlId}
      key={id}
      name={name || id}
      onBlur={blurHandler as any}
      onChange={changeHandler as any}
      onFocus={focusHandler as any}
      placeholder={firstDefined(placeholder, questionText)}
      type={type || 'text'}
      value={value as string}
    />
  );

  const controls = [control];
  if (suggestionList.length > 0) {
    controls.push(
      <div key={`list-${id}`} aria-labelledby={controlId} className={`${dropDownMenuCssClass} show`}>
        {suggestionList}
      </div>
    );
  }

  return (
    <StandardFormGroupWidget
      answerSource={answerSource}
      control={controls}
      controlType="auto-complete"
      dataType={dataType}
      error={error}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      id={id}
      onClick={onChange}
      question={question}
      questionText={questionText}
      subtext={subtext}
      textAndStyle={textAndStyle}
    />
  );
}

function createChangeEventPrelude(lookup: HpClientQuestionLookupItemMap) {
  return (e: BaseSyntheticEvent): PreludeActionResponse => {
    const { target, type: eventType } = e;
    const { id: targetId, value } = target;
    if (eventType !== 'blur') {
      return value;
    }

    const { relatedTarget } = e as unknown as MouseEvent;
    const targetChildIsFocused = relatedTarget && (relatedTarget as HTMLElement).id.startsWith(targetId);
    const maybeChangedValue = findLookupKeyFromText(lookup, value, { ignoreCase: true });
    const hasChanged = (maybeChangedValue !== undefined && maybeChangedValue !== value) || !targetChildIsFocused;
    return {
      hasChanged,
      value: maybeChangedValue || value
    };
  };
}

interface PreludeActionResponse {
  hasChanged: boolean;
  value: string | undefined;
}

function createCustomHandler<HandlerInT extends HpOnMouseEvent | HpOnFocusEvent>(
  question: HpClientQuestion,
  handler: HandlerInT | undefined,
  action: SlideActionTypes,
  preludeAction?: (e: BaseSyntheticEvent) => PreludeActionResponse | void
): (e: BaseSyntheticEvent) => void {
  const customHandler: (e: BaseSyntheticEvent) => void = (e) => {
    if (preludeAction) {
      const { hasChanged, value } = preludeAction(e) || {};
      if (value !== undefined) {
        e.target.value = value;
        (e as any).hpClearPartialAnswer = hasChanged;
      }
    }

    if (handler) {
      handler(e as any, { type: 'standard', question });
    }

    const target = $(e.target);
    const $list = $(`.${dropDownMenuCssClass}`, target.closest(autoCompleteCssSelector));
    const list = $list.get(0);

    if (list) {
      const withFocus = document.activeElement as HTMLElement | null;
      const itemHasFocus = withFocus ? $(dropDownItemCssSelector, list).toArray().indexOf(withFocus) >= 0 : false;
      if (!itemHasFocus) {
        if (action === slideUpAction) {
          $list.slideUp();
        } else {
          $list.slideDown();
        }
      }
    }
  };

  return customHandler;
}

export function addAutoCompleteKeyboardNavigationHandler(window: Window, jQuery: JQueryStatic): void {
  addKeyboardNavigationHandler({
    controlContainerCssSelector: autoCompleteCssSelector,
    controlItemCssSelector: dropDownItemCssSelector,
    noMatchKey,
    window,
    jQuery
  });
}
